import React, { useState, useEffect } from 'react';
import PropTypes, { object } from 'prop-types';

import cn from 'classnames';

import {
  Table,
  SortingIcon,
  EmptyState,
  Loading
} from 'components/common/Elements/Elements';
import TableRow from './TableRow';

import useWindowSize from 'hooks/useWindowSize';
import { calcColumnShadow, whoIslast } from 'utils/screenTableCalc';

import classes from './PlatformActivitiesTable.module.scss';

const PlatformActivitiesTable = ({
  handleSortable,
  loader,
  tableConf,
  filter,
  data = []
}) => {
  const { direction, fieldName } = filter;

  const [row, setRow] = useState('');
  const [columnShadow, setColumnShadow] = useState(true);
  const [firstColumnAbs, setFirstColumnAbs] = useState(true);
  const [lastColumn, setLastColumn] = useState('Additional Data');
  const [hoverColumn, setHoverColumn] = useState('');
  const size = useWindowSize();

  const mouseEnter = e => {
    setRow(e.currentTarget.getAttribute('r'));
    setHoverColumn(e.target.innerText);
  };
  const mouseLeave = () => {
    setRow('');
    setHoverColumn('');
  };

  useEffect(() => {
    const isColumnShadow = calcColumnShadow(tableConf, size.width);
    setColumnShadow(isColumnShadow);
  }, [size, tableConf]);

  useEffect(() => {
    const quantityColumns = tableConf.reduce(function (
      accumulator,
      currentValue
    ) {
      if (currentValue.show) return accumulator + 1;
      return accumulator;
    },
    0);
    if (quantityColumns === 1) setFirstColumnAbs(false);
    else setFirstColumnAbs(true);
  }, [tableConf]);

  const handleScroll = e => {
    if (columnShadow) {
      setLastColumn(whoIslast(e.target, tableConf));
    } else {
      setLastColumn('');
    }
  };

  return (
    <>
      <div className={classes.outer}>
        <div
          className={cn(
            classes.inner,
            columnShadow ? classes.inner_shadow : classes.inner_nowidth,
            !firstColumnAbs && classes.inner_noabs,
            lastColumn === hoverColumn && classes.inner_pad
          )}
          onScroll={handleScroll}
        >
          <Table className={classes.table}>
            <Table.Header>
              <Table.Row
                withBorder={false}
                className={cn(classes.row, classes.row_header)}
              >
                {tableConf.map(
                  ({ isSortable, field, title, show }, ind) =>
                    show && (
                      <Table.HeaderCell
                        className={cn(classes.headerCell, classes[field])}
                        onMouseEnter={mouseEnter}
                        onMouseLeave={mouseLeave}
                        r={`r${ind}`}
                        title={title}
                        key={field}
                      >
                        {isSortable && (
                          <SortingIcon
                            direction={direction}
                            field={field}
                            fieldName={fieldName}
                            onClick={handleSortable(field)}
                          />
                        )}
                      </Table.HeaderCell>
                    )
                )}
              </Table.Row>
            </Table.Header>
            {loader || data.length === 0 ? (
              <Table.Body></Table.Body>
            ) : (
              <Table.Body>
                {data.map((company, index) => (
                  <TableRow
                    key={index}
                    company={company}
                    tableConf={tableConf}
                    row={row}
                    mouseEnter={mouseEnter}
                  />
                ))}
              </Table.Body>
            )}
          </Table>
        </div>
      </div>

      {loader ? <Loading /> : data.length === 0 && <EmptyState />}
    </>
  );
};

PlatformActivitiesTable.propTypes = {
  data: PropTypes.arrayOf(object),
  tableConf: PropTypes.arrayOf(object),
  handleSortable: PropTypes.func,
  loader: PropTypes.bool,
  filter: PropTypes.object
};

export default PlatformActivitiesTable;
